import { useState } from 'react';
import axios from 'axios';
import { HelpCircle, X, Send, ChevronRight, AlertTriangle, RefreshCw, LogIn, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TROUBLESHOOT_TIPS = [
  { icon: LogIn, title: "Can't log in?", tip: "Try resetting your password. Make sure caps lock is off. Clear your browser cache and try again." },
  { icon: RefreshCw, title: "Bracket not saving?", tip: "Make sure you're logged in first. Click 'Save My Bracket' after making your picks. Try refreshing the page." },
  { icon: Wifi, title: "Page not loading?", tip: "Check your internet connection. Try refreshing with Ctrl+Shift+R (hard refresh). Clear browser cache." },
  { icon: AlertTriangle, title: "Seeing an error?", tip: "Try refreshing the page. If the error persists, use the 'Report Issue' form below and we'll fix it ASAP." },
];

export function HelpWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('main'); // main, tips, report
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const submitTicket = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in both fields');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/ticket`, {
        subject: subject.trim(),
        description: description.trim(),
        page_url: window.location.href,
        user_agent: navigator.userAgent
      });
      toast.success(res.data.message);
      setSubject('');
      setDescription('');
      setView('main');
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Help Button */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="help-widget-btn"
          className="fixed bottom-20 right-5 z-[999] bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-3.5 shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95">
          <HelpCircle size={22} />
        </button>
      )}

      {/* Help Panel */}
      {open && (
        <div className="fixed bottom-20 right-5 z-[999] w-80 max-h-[60vh] bg-card border border-border rounded-xl shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-5 duration-200" data-testid="help-panel">
          {/* Header */}
          <div className="bg-primary/10 px-4 py-3 flex items-center justify-between border-b border-border flex-shrink-0">
            <span className="font-mono text-sm uppercase tracking-wider font-bold">
              {view === 'main' ? 'Need Help?' : view === 'tips' ? 'Quick Fixes' : 'Report Issue'}
            </span>
            <button onClick={() => { setOpen(false); setView('main'); }} data-testid="help-close-btn"
              className="text-muted-foreground hover:text-foreground">
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4">
            {view === 'main' && (
              <div className="space-y-3">
                <button onClick={() => setView('tips')} data-testid="help-troubleshoot-btn"
                  className="w-full flex items-center justify-between bg-background border border-border rounded-lg p-3 hover:border-primary/50 transition-colors text-left">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} className="text-primary" />
                    <span className="font-mono text-xs uppercase">Troubleshooting</span>
                  </div>
                  <ChevronRight size={14} className="text-muted-foreground" />
                </button>

                <button onClick={() => setView('report')} data-testid="help-report-btn"
                  className="w-full flex items-center justify-between bg-background border border-border rounded-lg p-3 hover:border-accent/50 transition-colors text-left">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-accent" />
                    <span className="font-mono text-xs uppercase">Report an Issue</span>
                  </div>
                  <ChevronRight size={14} className="text-muted-foreground" />
                </button>

                <p className="text-[10px] text-muted-foreground text-center mt-3">
                  Issues are sent directly to the admin for fast resolution
                </p>
              </div>
            )}

            {view === 'tips' && (
              <div className="space-y-3">
                <button onClick={() => setView('main')} className="text-xs text-primary font-mono uppercase mb-1">
                  ← Back
                </button>
                {TROUBLESHOOT_TIPS.map((t, i) => (
                  <div key={i} className="bg-background border border-border rounded-lg p-3" data-testid={`tip-${i}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <t.icon size={14} className="text-primary flex-shrink-0" />
                      <span className="font-mono text-xs font-bold">{t.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{t.tip}</p>
                  </div>
                ))}
                <button onClick={() => setView('report')} className="w-full mt-2 text-xs text-accent font-mono uppercase text-center hover:underline">
                  Still having issues? Report it →
                </button>
              </div>
            )}

            {view === 'report' && (
              <div className="space-y-3">
                <button onClick={() => setView('main')} className="text-xs text-primary font-mono uppercase mb-1">
                  ← Back
                </button>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">What's the issue?</label>
                  <select value={subject} onChange={e => setSubject(e.target.value)} data-testid="ticket-subject"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-xs font-mono">
                    <option value="">Select a topic...</option>
                    <option value="Can't log in">Can't log in</option>
                    <option value="Bracket not saving">Bracket not saving</option>
                    <option value="Page error/crash">Page error or crash</option>
                    <option value="Payment issue">Payment issue</option>
                    <option value="Data looks wrong">Data looks wrong</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Tell us more</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} data-testid="ticket-description"
                    placeholder="Describe what happened..."
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-xs font-mono h-20 resize-none" />
                </div>
                <Button onClick={submitTicket} disabled={sending} data-testid="ticket-submit-btn"
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-mono text-xs uppercase gap-1.5">
                  <Send size={13} /> {sending ? 'Sending...' : 'Submit Report'}
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">
                  We'll be notified immediately and work to fix it fast
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
